import type { WerkflowInstanceSettings } from "@t3tools/contracts/settings";
import type {
  ComposerErpReference,
  ComposerErpReferenceEntityType,
} from "./composerErpReference.ts";
import { callWerkflowTool } from "./werkflow.ts";
import { WerkflowRestInvoker } from "./werkflowApi.ts";

export type WerkflowContextSearchStatus = "idle" | "loading" | "ready" | "unavailable" | "error";

export interface WerkflowContextSearchItem extends ComposerErpReference {
  readonly description: string;
}

export interface WerkflowContextSearchResult {
  readonly items: ReadonlyArray<WerkflowContextSearchItem>;
  readonly hadError: boolean;
}

interface SearchGroup {
  readonly entityType: ComposerErpReferenceEntityType;
  readonly toolId: string;
  readonly groupLabel: string;
  readonly args: (query: string) => Record<string, unknown>;
  readonly singleton?: boolean;
}

const LIST_GROUPS: ReadonlyArray<SearchGroup> = [
  {
    entityType: "customer",
    toolId: "customers.list",
    groupLabel: "Customers",
    args: (query) => (query ? { search: query } : {}),
  },
  {
    entityType: "project",
    toolId: "projects.list",
    groupLabel: "Projects",
    args: (query) => (query ? { search: query } : {}),
  },
  {
    entityType: "lead",
    toolId: "leads.list",
    groupLabel: "Leads",
    args: (query) => ({ includeClosed: true, ...(query ? { search: query } : {}) }),
  },
  {
    entityType: "billing",
    toolId: "billing.list",
    groupLabel: "Billing",
    args: (query) => ({ status: "Alle", ...(query ? { search: query } : {}) }),
  },
  {
    entityType: "inventory",
    toolId: "inventory.list",
    groupLabel: "Inventory",
    args: (query) => (query ? { search: query } : {}),
  },
  {
    entityType: "communication",
    toolId: "communication.listChannels",
    groupLabel: "Communication",
    args: (query) => (query ? { search: query } : {}),
  },
];

const SINGLETON_GROUPS: ReadonlyArray<SearchGroup> = [
  {
    entityType: "planning",
    toolId: "planning.get",
    groupLabel: "Planning",
    args: () => ({}),
    singleton: true,
  },
  {
    entityType: "dashboard",
    toolId: "dashboard.summary",
    groupLabel: "Dashboard",
    args: () => ({}),
    singleton: true,
  },
  {
    entityType: "settings",
    toolId: "settings.get",
    groupLabel: "Settings",
    args: () => ({}),
    singleton: true,
  },
];

const LIST_RESULT_KEYS = [
  "items",
  "results",
  "rows",
  "data",
  "customers",
  "projects",
  "leads",
  "documents",
  "billing",
  "inventory",
  "channels",
];

const FIELD_CANDIDATES: Record<
  ComposerErpReferenceEntityType,
  {
    readonly label: ReadonlyArray<string>;
    readonly description: ReadonlyArray<string>;
  }
> = {
  customer: {
    label: ["company", "name", "displayName", "contactName", "customerName"],
    description: ["contactName", "email", "phone", "address", "city"],
  },
  project: {
    label: ["project", "name", "title", "label", "address"],
    description: ["customer", "customerName", "status", "address", "responsible"],
  },
  lead: {
    label: ["company", "contactName", "project", "name"],
    description: ["stage", "project", "email", "phone", "address"],
  },
  billing: {
    label: ["number", "documentNumber", "kind", "project", "customerName"],
    description: ["kind", "status", "customer", "customerName", "project", "grossTotal", "total"],
  },
  inventory: {
    label: ["name", "itemName", "sku", "articleNumber", "label"],
    description: ["sku", "category", "supplier", "stock", "quantity", "unit"],
  },
  communication: {
    label: ["name", "label", "channel", "subject"],
    description: ["type", "customer", "project", "lastMessageAt", "updatedAt"],
  },
  planning: {
    label: ["label", "name", "title"],
    description: ["date", "status", "summary"],
  },
  dashboard: {
    label: ["label", "name", "title"],
    description: ["activeProjectCount", "openOfferCount", "overdueInvoiceCount"],
  },
  settings: {
    label: ["label", "name", "company", "tenantName"],
    description: ["currency", "timezone", "locale"],
  },
};

const ID_FIELDS = [
  "_id",
  "id",
  "documentId",
  "customerId",
  "projectId",
  "leadId",
  "legacyId",
  "number",
  "key",
];

function compactParts(parts: ReadonlyArray<string | null>): string {
  return parts.filter((part): part is string => Boolean(part && part.trim())).join(" · ");
}

function valueToString(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function readField(record: Record<string, unknown>, names: ReadonlyArray<string>): string | null {
  for (const name of names) {
    const value = valueToString(record[name]);
    if (value) return value;
  }
  return null;
}

function stableFallbackId(record: Record<string, unknown>, fallback: string): string {
  return readField(record, ID_FIELDS) ?? fallback;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function extractArrayResult(value: unknown): ReadonlyArray<unknown> {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  if (!record) return [];
  for (const key of LIST_RESULT_KEYS) {
    const maybeArray = record[key];
    if (Array.isArray(maybeArray)) return maybeArray;
  }
  return [];
}

function queryMatchesItem(query: string, item: WerkflowContextSearchItem): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const haystack =
    `${item.entityType} ${item.label} ${item.description} ${item.toolId}`.toLowerCase();
  return haystack.includes(normalizedQuery);
}

function queryMatchesGroup(query: string, group: SearchGroup): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  return `${group.entityType} ${group.groupLabel} ${group.toolId}`
    .toLowerCase()
    .includes(normalizedQuery);
}

function formatSingletonDescription(value: unknown): string {
  const record = asRecord(value);
  if (!record) return "Read-only ERP context";
  return compactParts(
    Object.entries(record)
      .slice(0, 4)
      .map(([key, raw]) => {
        const value = valueToString(raw);
        return value ? `${key}: ${value}` : null;
      }),
  );
}

function toSearchItem(
  group: SearchGroup,
  value: unknown,
  fallbackIndex: number,
): WerkflowContextSearchItem | null {
  if (group.singleton) {
    return {
      entityType: group.entityType,
      id: group.entityType,
      label: group.groupLabel,
      toolId: group.toolId,
      description: formatSingletonDescription(value) || "Read-only ERP context",
    };
  }

  const record = asRecord(value);
  if (!record) return null;
  const fields = FIELD_CANDIDATES[group.entityType];
  const id = stableFallbackId(record, `${group.entityType}-${fallbackIndex}`);
  const label = readField(record, fields.label) ?? `${group.groupLabel} ${fallbackIndex + 1}`;
  const description = compactParts(fields.description.map((field) => valueToString(record[field])));
  return {
    entityType: group.entityType,
    id,
    label,
    toolId: group.toolId,
    description,
  };
}

export function getActiveWerkflowSearchInstance(
  instances: ReadonlyArray<WerkflowInstanceSettings>,
  activeId: string | null,
): WerkflowInstanceSettings | null {
  const activeInstance =
    activeId === null ? null : (instances.find((instance) => instance.id === activeId) ?? null);
  if (
    !activeInstance ||
    !activeInstance.enabled ||
    activeInstance.apiUrl.trim().length === 0 ||
    activeInstance.apiKey.trim().length === 0
  ) {
    return null;
  }
  return activeInstance;
}

export async function searchWerkflowContextItems(
  instance: WerkflowInstanceSettings,
  query: string,
): Promise<WerkflowContextSearchResult> {
  const invoker = new WerkflowRestInvoker({
    apiUrl: instance.apiUrl,
    apiKey: instance.apiKey,
  });
  const groups = [
    ...LIST_GROUPS,
    ...SINGLETON_GROUPS.filter((group) => queryMatchesGroup(query, group)),
  ];
  let hadError = false;
  const groupResults = await Promise.all(
    groups.map(async (group) => {
      try {
        const value = await callWerkflowTool(invoker, group.toolId, group.args(query));
        const rawItems = group.singleton ? [value] : extractArrayResult(value);
        return rawItems
          .map((item, index) => toSearchItem(group, item, index))
          .filter((item): item is WerkflowContextSearchItem => item !== null)
          .filter((item) => queryMatchesItem(query, item))
          .slice(0, group.singleton ? 1 : 6);
      } catch {
        hadError = true;
        return [];
      }
    }),
  );

  const seen = new Set<string>();
  const items = groupResults.flat().filter((item) => {
    const key = `${item.entityType}:${item.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { items, hadError };
}
